import type { FC } from 'react';
import React from 'react';
import styled from 'styled-components';

import { Bubble, HintButton } from '@lt/components';
import CurrencyFormat from '../../../../components/CurrencyFormat';

interface PriceDetailsItemProps {
  className?: string;
  highlighted?: boolean;
  price?: number | string;
  label: string;
  isArchived?: boolean;
  isReduction?: boolean;
  tooltipText?: string;
}

const PriceDetailsItem: FC<PriceDetailsItemProps> = ({
  className,
  highlighted,
  price,
  children,
  label,
  isArchived = false,
  isReduction,
  tooltipText,
}) => (
  <StyledRow $isHighlighted={!!highlighted} className={className}>
    <StyledLabel title={label}>{label}</StyledLabel>
    <StyledPriceValue $highlighted={highlighted} $isArchived={isArchived}>
      {children || (
        <StyledCurrencyFormat
          value={price}
          prefix={isReduction ? '-' : '+'}
          isReduction={isReduction}
        />
      )}
      {tooltipText && (
        <StyledBubble text={tooltipText}>
          <HintButton />
        </StyledBubble>
      )}
    </StyledPriceValue>
  </StyledRow>
);

const StyledPriceValue = styled.div<{
  $isArchived: boolean;
  $highlighted?: boolean;
}>`
  display: flex;
  position: relative;
  white-space: pre;
  text-align: right;
  &:before {
    ${({ $highlighted, $isArchived }) =>
      $highlighted &&
      ($isArchived ? 'background: #ffffff;' : 'background: #f8e71c;')}
  }
`;

const StyledBubble = styled(Bubble)`
  margin-left: 8px;
`;

const StyledCurrencyFormat = styled(CurrencyFormat)<{
  isReduction?: boolean;
}>`
  ${({ isReduction }) =>
    !isReduction &&
    `background: #F8E71C;
    border-radius: 10px;
    padding: 2px;`}
`;

const StyledRow = styled.li<{ $isHighlighted: boolean }>`
  display: flex;
  margin-bottom: 6px;
  ${({ $isHighlighted }) =>
    $isHighlighted &&
    `
    &:first-child ${StyledPriceValue}::before {
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
    }
    &:last-child ${StyledPriceValue}::before {
      border-radius: 10px;
    }
    ${StyledPriceValue} {
      > * {
        position: relative;
        z-index: 1;
      }

      &::before {
        content: '';
        position: absolute;
        z-index: 0;
        left: -5px;
        top: -1px;
        bottom: -1px;
        right: -5px;
      }
    }
  `}
`;

const StyledLabel = styled.div`
  width: 100%;
  &::first-letter {
    text-transform: capitalize;
  }
`;

export default PriceDetailsItem;
